import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { translations } from '../lib/translations';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        const saved = localStorage.getItem('wellness_language');
        if (saved) return saved;
        // Detect browser language
        const browserLang = navigator.language.split('-')[0];
        return browserLang === 'de' ? 'de' : 'en';
    });

    useEffect(() => {
        localStorage.setItem('wellness_language', language);
        document.documentElement.lang = language;
    }, [language]);

    // Memoized: stable identity per language, safe to use in hook dependency arrays
    const t = useCallback((key, params = {}) => {
        let text = translations[language]?.[key] || translations['en']?.[key] || key;
        // Replace placeholders like {points} with actual values
        Object.entries(params).forEach(([k, v]) => {
            text = text.replace(`{${k}}`, v);
        });
        return text;
    }, [language]);

    const toggleLanguage = useCallback(() => {
        setLanguage(prev => prev === 'de' ? 'en' : 'de');
    }, []);

    const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t }), [language, toggleLanguage, t]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
